import {
  getTablePermissions,
  getIgnoredColumnsByPermission,
  filterColumnsByPermission,
  hasPermission,
} from "@/lib/permissions";
import type { UserRoleType } from "@/types/auth";
import { router } from "expo-router";
import { theme } from "@/constants/themes";
import { EnumOption } from "@/components/ui/EnumInput";
import { TIME_MODIFIERS, TIME_MODIFIER_CODES } from "@/types/db/time_cards";

// Build modifier options for table display
const MODIFIER_OPTIONS = TIME_MODIFIER_CODES.map((code) => ({
  value: code,
  label: `${TIME_MODIFIERS[code].label} (${TIME_MODIFIERS[code].multiplier}x)`,
  color: TIME_MODIFIERS[code].color,
}));

const STATUS_OPTIONS = [
  { value: "APPROVED", color: "complete" },
  { value: "PENDING", color: "pending" },
  { value: "REJECTED", color: "attention" },
] as const;

const BILLING_OPTIONS = [
  { value: "BILLABLE", color: "complete" },
  { value: "NON BILLABLE", color: "neutral" },
] as const;

// Exported for use in form components (EnumInput format)
export const getTimecardStatusOptions = (colors: theme): EnumOption[] => [
  { text: "PENDING", bgColor: colors.pending.border },
  { text: "APPROVED", bgColor: colors.complete.border },
  { text: "REJECTED", bgColor: colors.attention.border },
];

export const getTimecardBillingOptions = (colors: theme): EnumOption[] => [
  { text: "BILLABLE", bgColor: colors.complete.border },
  { text: "NON BILLABLE", bgColor: colors.neutral.border },
];

const BILLING_STATUS_OPTIONS = [
  { value: "PENDING", color: "neutral" },
  { value: "READY FOR BILLING", color: "todo" },
  { value: "BILLING IN PROGRESS", color: "pending" },
  { value: "PNL PENDING", color: "complete" },
] as const;

const PROJECT_STATUS_OPTIONS = [
  { value: "POTENTIAL", color: "todo" },
  { value: "PENDING APPROVAL", color: "pending" },
  { value: "IN PROGRESS", color: "pending" },
  { value: "ON HOLD", color: "attention" },
  { value: "COMPLETED", color: "complete" },
  { value: "CONFLICTING", color: "attention" },
  { value: "LOST", color: "neutral" },
  { value: "CANCELED", color: "attention" },
] as const;

const SHIFT_USER_STATUS_OPTIONS = [
  { value: "QUEUED", color: "neutral" },
  { value: "INVITED", color: "pending" },
  { value: "ACCEPTED", color: "complete" },
  { value: "DECLINED", color: "attention" },
  { value: "CANCELLED", color: "attention" },
  { value: "NOT NEEDED", color: "neutral" },
  { value: "NO RESPONSE", color: "attention" },
  { value: "NO SHOW", color: "attention" },
  { value: "ATTENDANCE EXCEPTION", color: "attention" },
  { value: "COMPLETE", color: "todo" },
] as const;

// Columns that require read permission on related entities
const RELATIONSHIP_COLUMNS = {
  user: "users",
  user_id: "users",
  project: "projects",
  project_id: "projects",
  project_status: "projects",
  project_billing_status: "projects",
  client: "clients",
  client_id: "clients",
  shift: "shifts",
  shift_id: "shifts",
  position: "positions",
  position_id: "positions",
} as const;

export type MarketOption = { value: string; label: string };

export const getTimecardTableConfig = (
  userRoles: UserRoleType[] | null,
  marketOptions: MarketOption[] = [],
  displayNames,
) => {
  const permissions = getTablePermissions(userRoles, "time_cards");

  // Get columns to hide based on relationship permissions
  const permissionIgnored = getIgnoredColumnsByPermission(userRoles, RELATIONSHIP_COLUMNS);

  // Build readonly array conditionally based on permissions
  const readonlyColumns: (string | [string, (key: string, value: any, row: any) => void])[] = [
    // "created_at",
    // "start_time", // Now ignored (derived from time)
    // "meal_break", // Now ignored (derived from time)
    // "end_time", // Now ignored (derived from time)
    // "hours_worked", // Now ignored (derived from time)
    "billing_items",
    "payroll_items",
    "billing_amount",
    "payroll_amount",
    "start_time",
    "end_time",
  ];

  // Only add clickable client column if user has clients.read
  if (hasPermission(userRoles, "clients", "read")) {
    readonlyColumns.push(["client", (_key, _value, row) => router.setParams({ panel: "client", id: row.client?.id })]);
  }

  // Only add clickable project columns if user has projects.read
  if (hasPermission(userRoles, "projects", "read")) {
    readonlyColumns.push(
      "project_id",
      ["project", (_key, _value, row) => router.setParams({ panel: "project", id: row.project_id })],
      "project_status",
      "project_billing_status",
    );
  }

  // Only add clickable shift column if user has shifts.read
  if (hasPermission(userRoles, "shifts", "read")) {
    readonlyColumns.push(["shift", (_key, _value, row) => router.setParams({ panel: "shift", id: row.shift_id })]);
  }

  // Only add clickable user column if user has users.read
  if (hasPermission(userRoles, "users", "read")) {
    readonlyColumns.push("user_id", [
      "user",
      (_key, _value, row) => router.setParams({ panel: "user", id: row.user?.id }),
    ]);
  }

  // Add shift_user_status as readonly (right after user)
  readonlyColumns.push("shift_user_status");

  // Only add position column if user has positions.read
  if (hasPermission(userRoles, "positions", "read")) {
    readonlyColumns.push("position");
  }

  // Markets are always readonly (inherited from shift->project)
  readonlyColumns.push("markets");

  return {
    entityName: "Time_card",
    entityNamePlural: "Time_cards",

    tableConfig: {
      headerFunc: (name: string) => {
        switch (name) {
          case "client":
            return displayNames?.clients?.name ?? "Client";
          case "project":
            return displayNames?.projects?.name ?? "Project";
          case "project_status":
            return `${displayNames?.projects?.name ?? "Project"} Status`;
          case "project_billing_status":
            return `${displayNames?.projects?.name ?? "Project"} Billing Status`;
          case "shift":
            return displayNames?.shifts?.name ?? "Shift";
          case "shift_user_status":
            return `${displayNames?.shifts?.name ?? "Shift"} ${displayNames?.users?.name ?? "User"} Status`;
          case "user":
            return displayNames?.users?.name ?? "User";
          default:
            return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        }
      },
      // anchor: ["start_time"],
      ignore: [
        "id",
        "tenant_id",
        "meta_data",
        "position_id",
        "position_data",
        "position_snapshot",
        "user_pay_snapshot",
        "shift_id",
        "project_id",
        "client_id",
        // Derived from time JSONB - meal_break and hours_worked still ignored
        "meal_break",
        "hours_worked",
        // Individual rate columns hidden - use combined "rates" column with RateEditor
        "billing_rate",
        "payroll_rate",
        ...permissionIgnored,
      ],
      readonly: readonlyColumns,
      // headerActions moved to time_cards.tsx component to access useToast() hook
      validators: {},
      writeKeys: {
        notes: "meta_data.note",
        modifiers: "meta_data.modifiers",
        time: "time",
        rates: "meta_data", // RateEditor saves billing_rate_override and payroll_rate_override to meta_data
      },
      cellTextCallback: (value: any, key: string) => {
        if (value == null) return "";
        if (key === "hours_worked") return typeof value === "number" ? value.toFixed(2) : "";
        if (key === "billing_rate" && typeof value === "object") {
          const suffix = value.rate === "daily" ? "/day" : "/hr";
          return `$${value.amount}${suffix}`;
        }
        if (key === "payroll_rate" && typeof value === "object") {
          if (value.rate === "base_rate") return `${value.multiplier}x base (legacy)`;
          if (value.rate === "base_add") return `+$${value.offset}/hr`;
          const suffix = value.rate === "daily" ? "/day" : "/hr";
          return `$${value.amount}${suffix}`;
        }
        if (key === "billing_amount" || key === "payroll_amount") {
          return typeof value === "number" ? `$${value.toFixed(2)}` : "";
        }
        if (key === "billing_items" || key === "payroll_items") {
          return Array.isArray(value) ? `${value.length}` : "0";
        }
        if (key === "project_id") return value.toString().slice(0, 6).toUpperCase();
        if (key === "user_id") return value.toString().slice(0, 6).toUpperCase();
        if (key === "shift_id") return value.toString().slice(0, 6).toUpperCase();
        return typeof value === "string" ? value : JSON.stringify(value);
      },
      dataTypes: {
        user: {
          type: "linked_record" as const,
          labelKey: "name",
          valueKey: "id",
          truncate: 6,
        },
        client: {
          type: "linked_record" as const,
          labelKey: "name",
          valueKey: "id",
          truncate: 6,
        },
        project: {
          type: "linked_record" as const,
          labelKey: "name",
          valueKey: "id",
          truncate: 6,
        },
        shift: {
          type: "linked_record" as const,
          labelKey: "name",
          valueKey: "id",
          truncate: 6,
        },
        time: {
          type: "time_object" as const,
          showDuration: true,
        },
        rates: {
          type: "rate_object" as const,
        },
        // modifiers: {
        //   type: "array" as const,
        //   picker: "time_modifier" as const,
        //   chipColor: "#6B7280",
        //   options: MODIFIER_OPTIONS,
        // },
        start_time: "date-time",
        end_time: "date-time",
        // meal_break: {
        //   type: "array" as const,
        //   chipColor: "#6B7280",
        // },
        billing_items: {
          type: "number",
        },
        // billing: {
        //   type: "enum",
        //   options: BILLING_OPTIONS,
        // },
        status: {
          type: "enum",
          options: STATUS_OPTIONS,
        },
        project_status: {
          type: "enum",
          options: PROJECT_STATUS_OPTIONS,
        },
        project_billing_status: {
          type: "enum",
          options: BILLING_STATUS_OPTIONS,
        },
        shift_user_status: {
          type: "enum",
          options: SHIFT_USER_STATUS_OPTIONS,
        },
        approved: {
          type: "boolean",
          trueColor: "complete",
          falseColor: "attention",
        },
        markets: {
          type: "array" as const,
          chipColor: "#4169E1", // Royal blue to match other market chips
          options: marketOptions,
        },
      },
    },

    filterChoices: filterColumnsByPermission(
      userRoles,
      [
        { name: "modifiers", kind: "text" as const, menus: ["visible"] as const },
        { name: "start_time", kind: "date" as const, menus: ["filter", "visible", "orderby", "groupby"] as const },
        { name: "end_time", kind: "date" as const, menus: ["filter", "visible", "orderby", "groupby"] as const },
        // { name: "meal_break", kind: "text" as const, menus: ["visible"] as const },
        { name: "rates", kind: "text" as const, menus: ["visible"] as const },
        {
          name: "markets",
          kind: "array" as const,
          displayName: "Markets",
          options: marketOptions,
          menus: ["filter", "visible", "groupby"] as const,
        },
        { name: "billing_items", kind: "number" as const, menus: ["filter", "visible", "orderby"] as const },
        { name: "hours_worked", kind: "number" as const, menus: ["filter", "visible", "orderby", "groupby"] as const },
        {
          name: "status",
          kind: "enum" as const,
          options: [...STATUS_OPTIONS],
          menus: ["filter", "visible", "orderby", "groupby"] as const,
        },
        {
          name: "billing",
          kind: "enum" as const,
          options: [...BILLING_OPTIONS],
          menus: ["filter", "visible", "orderby", "groupby"] as const,
        },
        {
          name: "project_status",
          displayName: `${displayNames?.projects?.name ?? "Project"} Status`,
          kind: "enum" as const,
          options: [...PROJECT_STATUS_OPTIONS],
          menus: ["filter", "visible", "orderby", "groupby"] as const,
        },
        {
          name: "project_billing_status",
          displayName: `${displayNames?.projects?.name ?? "Project"} Billing Status`,
          kind: "enum" as const,
          options: [...BILLING_STATUS_OPTIONS],
          menus: ["filter", "visible", "groupby", "orderby"] as const,
        },
        {
          name: "shift_user_status",
          displayName: `${displayNames?.shifts?.name ?? "Shift"} ${displayNames?.users?.name ?? "User"} Status`,
          kind: "enum" as const,
          options: [...SHIFT_USER_STATUS_OPTIONS],
          menus: ["filter", "visible", "orderby", "groupby"] as const,
        },
        {
          name: "shift",
          displayName: displayNames?.shifts?.name ?? "Shift",
          kind: "linked_record" as const,
          menus: ["filter", "visible", "groupby", "orderby"] as const,
          linkedRecord: { labelKey: "name", valueKey: "id", truncate: 6 },
        },
        {
          name: "project",
          displayName: displayNames?.projects?.name ?? "Project",
          kind: "linked_record" as const,
          menus: ["filter", "visible", "groupby", "orderby"] as const,
          linkedRecord: { labelKey: "name", valueKey: "id", truncate: 6 },
        },
        {
          name: "client",
          displayName: `${displayNames?.clients?.name ?? "Client"}`,
          kind: "linked_record" as const,
          menus: ["filter", "visible", "groupby", "orderby"] as const,
          linkedRecord: { labelKey: "name", valueKey: "id", truncate: 6 },
        },
        { name: "position", kind: "text" as const, menus: ["filter", "visible"] as const },
        {
          name: "user",
          displayName: `${displayNames?.users?.name ?? "User"}`,
          kind: "linked_record" as const,
          menus: ["filter", "visible", "groupby", "orderby"] as const,
          linkedRecord: { labelKey: "name", valueKey: "id", truncate: 6 },
        },
        { name: "created_at", kind: "date" as const, menus: ["filter", "visible", "orderby"] as const },
      ],
      RELATIONSHIP_COLUMNS,
    ),

    ...permissions,
    storageKey: "timecard_flyout",
  };
};
